import type { LineItemsColumn, PriceBookColumn, RolesResponsibilitiesContent } from '@/blocks/domain/types';
import type { BlockContentDto, TableColumnDto } from '@/blocks/application/mappers/blockMapper';
import { mergeLineItemsColumnsWithDefaults, normalizeLineItemsColumnKey } from '@/blocks/domain/helpers/lineItemsColumnKeys';
import { normalizePriceBookColumnKey } from '@/blocks/domain/helpers/priceBookColumnKeys';

export function resolvePriceBookColumnOrder(raw: TableColumnDto & { sort_order?: number }, fallbackIndex: number): number {
  if (typeof raw.sortOrder === 'number') return raw.sortOrder;
  if (typeof raw.sort_order === 'number') return raw.sort_order;
  return fallbackIndex;
}

function resolveLineItemsColumnOrder(raw: TableColumnDto & { sort_order?: number }, fallbackIndex: number): number {
  if (typeof raw.sortOrder === 'number') return raw.sortOrder;
  if (typeof raw.sort_order === 'number') return raw.sort_order;
  return fallbackIndex;
}

export function mapLineItemsColumns(dtoColumns: Extract<BlockContentDto, { kind: 'line_items_table' }>): LineItemsColumn[] {
  const mapped = dtoColumns.columns.map((col, idx): LineItemsColumn => {
    const raw = col as TableColumnDto & { sort_order?: number };
    return {
      key: normalizeLineItemsColumnKey(col.key),
      visible: col.visible,
      label: col.label,
      order: resolveLineItemsColumnOrder(raw, idx)
    };
  });

  return mergeLineItemsColumnsWithDefaults(mapped);
}

export function mapPriceBookColumns(dtoColumns: Extract<BlockContentDto, { kind: 'price_book' }>): PriceBookColumn[] {
  // The API's default price_book content (`{ kind, title, entries: [] }`) carries no `columns`; a retired block must
  // still open, so a missing list maps to none and the defaults are merged in by the caller.
  const dtoList = Array.isArray(dtoColumns.columns) ? dtoColumns.columns : [];
  return dtoList.map((col, idx): PriceBookColumn => {
    const raw = col as TableColumnDto & { sort_order?: number };
    const order = resolvePriceBookColumnOrder(raw, idx);

    return {
      key: normalizePriceBookColumnKey(col.key),
      visible: col.visible,
      label: col.label,
      order
    };
  });
}

const DEFAULT_ROLES_COLUMNS: RolesResponsibilitiesContent['columns'] = {
  role: { visible: true, label: 'Role' },
  responsibility: { visible: true, label: 'Responsibility' }
};

export function mapRolesColumns(
  dtoColumns: Extract<BlockContentDto, { kind: 'roles_responsibilities' }>
): RolesResponsibilitiesContent['columns'] {
  const columns = dtoColumns.columns;
  if (columns == null || typeof columns !== 'object') return DEFAULT_ROLES_COLUMNS;

  if (Array.isArray(columns)) {
    const role = columns.find((c) => c.key === 'role');
    const responsibility = columns.find((c) => c.key === 'responsibility');

    return {
      role: {
        visible: role?.visible ?? true,
        label: role?.label ?? 'Role'
      },
      responsibility: {
        visible: responsibility?.visible ?? true,
        label: responsibility?.label ?? 'Responsibility'
      }
    };
  }

  return {
    role: {
      visible: columns.role?.visible ?? true,
      label: columns.role?.label ?? 'Role'
    },
    responsibility: {
      visible: columns.responsibility?.visible ?? true,
      label: columns.responsibility?.label ?? 'Responsibility'
    }
  };
}

export function fromRolesColumns(
  dto: RolesResponsibilitiesContent['columns']
): Extract<BlockContentDto, { kind: 'roles_responsibilities' }>['columns'] {
  return {
    role: {
      visible: dto.role.visible,
      label: dto.role.label
    },
    responsibility: {
      visible: dto.responsibility.visible,
      label: dto.responsibility.label
    }
  };
}
